<template>
  <List
    class="microsite-list"
    titleI18nKey="admin.crm.menu.micrositeManagement"
    createBtnLabelI18nKey="admin.crm.btn.microsite.add"
    :loading.sync="loading"
    :listData="listData"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :pagination="pagination"
    @create="handleCreate"
    @pagination-change="fetchListData"
  >
    <template #filter>
      <div class="filter-group">
        <el-select
          v-model="search.group"
          clearable
          filterable
          popper-class="search-group-select"
          :placeholder="$t('admin.crm.placeholder.microsite.group')"
          @change="handelChangeGroup"
        >
          <el-option
            v-for="(group, index) in groupList" :key="index"
            :label="group.i18nName"
            :value="group.id"
          >
            <div class="group-option">
              <span class="label">{{ group.i18nName }}</span>
              <div class="action-btn">
                <i class="group-edit-btn el-icon-edit" @click.stop="handleSaveEditGroupDialog(group)"></i>
                <i class="group-delete-btn el-icon-delete"  @click.stop="handleDeleteGroupDialog(group)"></i>
              </div>
            </div>
          </el-option>
        </el-select>
      </div>
      <div class="filter-keywords">
        <el-input
          v-model="search.keywords"
          clearable
          :placeholder="$t('admin.crm.placeholder.keywords')"
          @change="fetchListData"
          @keypress.enter.native="fetchListData"
        >
          <template #prefix>
            <i class="el-input__icon el-icon-search"></i>
          </template>
        </el-input>
      </div>
    </template>
    <template #btn-before>
      <el-button class="add-group-btn" @click="handleAddGroup">{{ $t('admin.crm.btn.microsite.addGroup') }}</el-button>
    </template>
    <template #isPublish="{ row }">
      <span class="status-container">
        <el-switch
          v-model="row.is_publish"
          class="status-switch"
          :width="34"
          @change="handleSwitch(row)"
        ></el-switch>
      </span>
    </template>
    <template #link="{ row }">
      <a class="link" :href="row.link" target="view_window">{{ row.link }}</a>
      <span class="site-visibility">{{ $t(`admin.crm.micrositeVisibility.${row.visibility}`) }}</span>
    </template>
    <template #page-bottom>
      <DialogWrapper
        titleI18nKey="admin.crm.message.microsite.deleteConfirm"
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :loading="deleteDialog.loading"
        :show.sync="deleteDialog.show"
        @save="handleDelete"
      ></DialogWrapper>
      <DialogWrapper
        titleI18nKey="common.btn.copy"
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :closeOnClickModal="false"
        :loading="copyDialog.loading"
        :show.sync="copyDialog.show"
        @closed="onCopyDialogClose"
        @save="handleCopy"
      >
        <el-form class="copy-form" ref="copyDialogForm" :model="copyDialog.form" :rules="copyDialogFormRules">
          <el-form-item :label="$t('admin.crm.label.microsite.micrositeName')" prop="name">
            <el-input v-model="copyDialog.form.name" :placeholder="$t('admin.crm.placeholder.microsite.micrositeName')" ></el-input>
          </el-form-item>
        </el-form>
      </DialogWrapper>
       <DialogWrapper
        closeOnCancel
        :titleI18nKey="groupDialog.titleI18nKey"
        :closeOnClickModal="false"
        :loading="groupDialog.loading"
        :show.sync="groupDialog.show"
        @closed="cleanGroupDialog"
        @save="handelSaveGroup"
      >
        <el-form ref="form" class="group-dialog-body" :model="groupDialog" :rules="formRules">
          <el-form-item class="list-item" prop="cnName" :label="$t('admin.crm.label.microsite.cnGroupName')">
            <el-input v-model="groupDialog.cnName" maxlength="100" :placeholder="$t('admin.crm.placeholder.microsite.groupName')"></el-input>
          </el-form-item>
          <el-form-item class="list-item" prop="name" :label="$t('admin.crm.label.microsite.enGroupName')">
            <el-input v-model="groupDialog.name" maxlength="100" :placeholder="$t('admin.crm.placeholder.microsite.groupName')"></el-input>
          </el-form-item>
        </el-form>
      </DialogWrapper>
      <DialogWrapper
        saveBtnLabelI18nKey="common.btn.confirm"
        titleI18nKey="admin.crm.message.microsite.deleteGroup"
        closeOnCancel
        :show.sync="groupDeleteDialog.show"
        :loading="groupDeleteDialog.loading"
        @save="handleDeleteGroup"
      ></DialogWrapper>
    </template>
  </List>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { MICROSITE_PATH } from '@/constant/crm'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import List from '@/components/tableList/allowDragSortTable.vue'

export default {
  components: {
    List,
    DialogWrapper
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-select-dropdown.el-popper.search-group-select{max-width: 429px;}' }
      ]
    }
  },
  data () {
    return {
      loadingCount: 0,
      groupList: [],
      listData: [],
      actionBtn: {
        edit: {
          onclick: this.handleEdit
        },
        delete: {
          show: (row) => !row.is_publish,
          onclick: this.openDeleteDialog
        },
        copy: {
          labelI18nKey: 'common.btn.copy',
          onclick: this.openCopyDialog
        }
      },
      search: {
        keywords: null,
        group: null
      },
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      },
      deleteDialog: {
        show: false,
        loading: false,
        data: null,
        dataIndex: null
      },
      copyDialog: {
        show: false,
        data: null,
        loading: false,
        form: {}
      },
      groupDialog: {
        show: false,
        loading: false,
        titleI18nKey: null,
        name: null,
        cnName: null,
        id: null
      },
      groupDeleteDialog: {
        show: false,
        loading: false,
        id: null
      },
      formRules: {
        name: [{ required: true, message: this.$t('admin.crm.placeholder.microsite.groupName') }],
        cnName: [{ required: true, message: this.$t('admin.crm.placeholder.microsite.groupName') }]
      }
    }
  },
  computed: {
    loading: {
      get () {
        return this.loadingCount > 0
      },
      set (val) {
        this.loadingCount += val ? 1 : -1
      }
    },
    tableColumns () {
      const columns = [
        {
          labelI18nKey: 'admin.crm.label.microsite.micrositeName',
          dataKey: 'name',
          width: 100
        },
        {
          labelI18nKey: 'admin.crm.label.microsite.micrositeLink',
          dataKey: 'link',
          slotName: 'link',
          width: 150
        },
        {
          labelI18nKey: 'admin.crm.label.pv',
          dataKey: 'pv',
          formatNumber: true
        },
        {
          labelI18nKey: 'admin.crm.label.application',
          dataKey: 'applied',
          formatNumber: true
        },
        {
          labelI18nKey: 'admin.crm.label.microsite.publish',
          dataKey: 'is_publish',
          slotName: 'isPublish'
        },
        {
          labelI18nKey: 'admin.crm.label.microsite.group',
          dataKey: 'group.i18nName'
        }
      ]
      return columns
    },
    copyDialogFormRules () {
      const rules = {
        name: [
          { required: true, message: this.$t('admin.crm.message.microsite.micrositeName') }
        ]
      }
      return rules
    },
    groupId () {
      const groupId = this.$route.query.group_id
      return groupId ? parseInt(groupId) : null
    }
  },
  created () {
    if (this.groupId) {
      this.search.group = this.groupId
    }
    this.loading = true
    Promise.all([
      this.initGroupList(),
      this.fetchListData()
    ]).finally(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions('crm', ['searchMicrosite', 'publishMicrosite', 'deleteMicrosite', 'copyMicrosite', 'getMicrositeGroupList', 'createMicrositeGroup', 'updateMicrositeGroup', 'deleteMicrositeGroup']),
    async initGroupList () {
      this.loading = true
      try {
        const resp = await this.getMicrositeGroupList()
        if (resp) {
          this.groupList = _.map(resp.list, group => {
            addI18nProperty(group, 'i18nName', 'cn_name', 'name')
            return group
          })
        }
      } finally {
        this.loading = false
      }
    },
    async fetchListData () {
      this.loading = true
      try {
        const resp = await this.searchMicrosite({
          keywords: this.search.keywords,
          group: this.search.group,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        if (resp) {
          const url = `${window.location.origin}${MICROSITE_PATH.INDEX}`
          this.pagination.total = resp.total
          this.listData = _.map(resp.data, item => {
            addI18nProperty(item.group, 'i18nName', 'cn_name', 'name')
            return {
              ...item,
              link: `${url}${item.short_unique_id}`
            }
          })
        }
      } finally {
        this.loading = false
      }
    },
    handleCreate () {
      this.$router.push({ name: 'addMicrosite' })
    },
    handleEdit (rowData) {
      this.$router.push({
        name: 'editMicrosite',
        params: {
          micrositeId: rowData.id
        }
      })
    },
    handleSwitch (rowData) {
      this.publishMicrosite(rowData)
    },
    openDeleteDialog (rowData, index) {
      this.deleteDialog.data = rowData
      this.deleteDialog.dataIndex = index
      this.deleteDialog.show = true
    },
    async handleDelete () {
      this.deleteDialog.loading = true
      try {
        await this.deleteMicrosite(this.deleteDialog.data.id)
        this.$message.success(this.$t('admin.crm.message.microsite.deleteSuccess'))
        this.listData.splice(this.deleteDialog.dataIndex, 1)
        this.deleteDialog.show = false
        this.deleteDialog.data = null
        this.deleteDialog.dataIndex = null
      } catch (err) {
        this.$message.error(this.$t('admin.crm.message.microsite.hasBindCampaign'))
      } finally {
        this.deleteDialog.loading = false
      }
    },
    openCopyDialog (rowData) {
      this.copyDialog.data = rowData
      this.copyDialog.form = {}
      this.copyDialog.show = true
    },
    onCopyDialogClose () {
      this.copyDialog.data = null
      this.copyDialog.form = {}
      this.$refs.copyDialogForm.clearValidate()
    },
    async handleCopy () {
      this.copyDialog.loading = true
      try {
        let result = false
        this.$refs.copyDialogForm.validate((isSuccess) => { result = isSuccess })
        if (!result) {
          return
        }
        const formId = this.copyDialog.data.id
        const newSite = this.copyDialog.form
        const resp = await this.copyMicrosite({ formId, newSite })
        if (resp) {
          this.listData = [{
            ...resp,
            link: `${window.location.origin}${MICROSITE_PATH.INDEX}${resp.short_unique_id}`
          }].concat(this.listData)
          this.copyDialog.data = null
          this.copyDialog.form = {}
          this.copyDialog.show = false
        }
      } finally {
        this.copyDialog.loading = false
      }
    },
    cleanGroupDialog () {
      const groupDialog = this.groupDialog
      groupDialog.cnName = null
      groupDialog.name = null
      groupDialog.id = null
      groupDialog.titleI18nKey = null
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleAddGroup () {
      const groupDialog = this.groupDialog
      groupDialog.titleI18nKey = 'admin.crm.label.microsite.addGroup'
      groupDialog.show = true
    },
    handleSaveEditGroupDialog (groupInfo) {
      const groupDialog = this.groupDialog
      groupDialog.name = groupInfo.name
      groupDialog.cnName = groupInfo.cn_name
      groupDialog.id = groupInfo.id
      groupDialog.titleI18nKey = 'admin.crm.label.microsite.editGroup'
      groupDialog.show = true
    },
    handleDeleteGroupDialog (groupInfo) {
      if (!groupInfo?.id) {
        return
      }

      const groupDeleteDialog = this.groupDeleteDialog
      groupDeleteDialog.id = groupInfo.id
      groupDeleteDialog.show = true
    },
    async handelSaveGroup () {
      const groupDialog = this.groupDialog
      groupDialog.loading = true
      try {
        const form = this.$refs.form
        let result = false
        form.validate((isSuccess) => {
          result = isSuccess
        })
        if (!result) {
          return
        }
        let saveMethod = this.createMicrositeGroup
        const params = {
          name: groupDialog.name,
          cn_name: groupDialog.cnName
        }
        let message = this.$t('admin.crm.message.microsite.addSuccess')
        if (groupDialog.id) {
          params.id = groupDialog.id
          message = this.$t('admin.crm.message.microsite.updateSuccess')
          saveMethod = this.updateMicrositeGroup
        }
        const resp = await saveMethod(params)
        if (resp && resp.status) {
          if (resp.status === 'success') {
            this.$message.success(message)
            this.groupDialog.show = false
            this.initGroupList()
          } else {
            this.$message.error(resp.message)
          }
        }
      } finally {
        groupDialog.loading = false
      }
    },
    async handleDeleteGroup () {
      const groupDeleteDialog = this.groupDeleteDialog
      groupDeleteDialog.loading = true
      try {
        const resp = await this.deleteMicrositeGroup(groupDeleteDialog.id)
        if (resp && resp.status) {
          if (resp.status === 'success') {
            this.$message.success(this.$t('admin.crm.message.microsite.deleteSuccess'))
            groupDeleteDialog.id = ''
            groupDeleteDialog.show = false
            this.initGroupList()
          } else {
            this.$message.error(resp.message)
          }
        }
      } finally {
        groupDeleteDialog.loading = false
      }
    },
    handelChangeGroup () {
      this.$router.replace({ query: { ...this.$route.query, group_id: this.search.group } })
      this.fetchListData()
    }
  }
}
</script>

<style lang="scss" scoped>
.microsite-list {
  .site-visibility {
    display: block;
  }

  .status-switch {
    ::v-deep {
      &.is-checked  {
        .el-switch__core {
          &::after {
            margin-left: -15px;
          }
        }
      }
      .el-switch__core {
        height: 16px;

        &::after {
          width: 14px;
          height: 14px;
          top: 0;
        }
      }
    }
  }
}

.group-dialog-body,
.copy-form {
  ::v-deep {
    .el-form-item {
      &:not(.is-no-asterisk) {
        .el-form-item__label {

          &::after {
            content: '*';
            margin: 9px 0 0 3px;
            color: #f56c6c;
            line-height: 1;
            visibility: hidden;
          }
        }
      }

      &.is-required:not(.is-no-asterisk) {
        .el-form-item__label {
          &::before {
            display: none;
          }

          &::after {
            visibility: unset;
          }
        }
      }
    }
  }
}

.search-group-select {
  .el-select-dropdown__item {
    padding: 0 10px;
  }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .group-option {
    display: flex;
    justify-content: space-between;

    .action-btn {
      line-height: inherit;
      margin-left: 10px;

      .group-edit-btn {
        line-height: inherit;
        font-size: 18px;
        margin-right: 20px;

        &:hover {
          color: #0096CC;
          cursor: pointer;
        }
      }

      .group-delete-btn {
        line-height: inherit;
        font-size: 18px;

        &:hover {
          color: #0096CC;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
